import {
  Loader,
  NoContentCard,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import {
  ResourceInfo,
  TopicMiniInfoV2,
} from '@protos/content_management/content.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useDeserifiedSelector } from '../../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { isAssignableResource, MAX_LEARNING_HW_RESOURCES } from '../../utils';
import SelectableResourceCard from './SelectableResourceCard';
import TopicSection from './TopicSection';
import { LockedIds, useContentLocks } from './useContentLocks';

// Chapter resources, then each topic's resources and lesson plans (demo only).
// A lesson plan is not stored as such: ticking it adds all of its resource ids.
interface IProps {
  subjectId: number;
  chapterId: number;
  selectedResourceIds: string[];
  onChange: (next: string[]) => void;
  onPreview: (resourceId: string, topicId?: number) => void;
}

interface ResourceGroup {
  title: string;
  resources: ResourceInfo[];
}

export default function ResourcePicker(props: IProps) {
  const { subjectId, chapterId, selectedResourceIds, onChange, onPreview } =
    props;
  const dispatch = useAppDispatch();
  const teacher_id = getLocalStorage('userId');
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { ContentCommonAPIServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const fetchLocks = useContentLocks();
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [topics, setTopics] = useState<TopicMiniInfoV2[]>([]);
  const [locked, setLocked] = useState<LockedIds>({
    resourceIds: new Set(),
    lessonIds: new Set(),
  });

  const selected = useMemo(
    () => new Set(selectedResourceIds),
    [selectedResourceIds]
  );
  const limitReached = selected.size >= MAX_LEARNING_HW_RESOURCES;

  useEffect(() => {
    (async () => {
      try {
        setLoading('loading');
        const [resourceRes, topicRes] = await Promise.all([
          ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterResources(
            {
              personId: BigInt(teacher_id),
              personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              subjectId,
              chapterId,
              sectionId: class_subject_info?.sectionId,
            }
          ),
          ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterTopicInfoV2(
            {
              personId: Number(teacher_id),
              personType: 'TEACHER',
              subjectId,
              chapterId,
              sectionId: class_subject_info?.sectionId,
              bookId: class_subject_info?.bookId,
              isAssessFlow: false,
            }
          ),
        ]);
        const seen = new Set<string>();
        const chapterGroups = (resourceRes?.data?.categoryResourceMap ?? [])
          .map((category) => ({
            title: category.categoryTitle,
            resources: category.categoryResources.filter((resource) => {
              // The same resource can appear under more than one category.
              if (
                !isAssignableResource(resource, class_subject_info?.sectionId) ||
                seen.has(resource.resourceId)
              )
                return false;
              seen.add(resource.resourceId);
              return true;
            }),
          }))
          .filter((group) => group.resources.length > 0);
        setGroups(chapterGroups);
        setTopics(
          [...(topicRes?.data?.topics ?? [])].sort(
            (a, b) => a.topicNo - b.topicNo
          )
        );
        setLocked(await fetchLocks([...seen], []));
        setLoading('completed');
      } catch (err) {
        console.log(err);
        setLoading('error');
      }
    })();
  }, [subjectId, chapterId]);

  const add = (ids: string[]) => {
    const next = new Set(selected);
    ids.forEach((id) => next.add(id));
    if (next.size > MAX_LEARNING_HW_RESOURCES) {
      dispatch(
        setToastInfo({
          label: `You can assign up to ${MAX_LEARNING_HW_RESOURCES} resources`,
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    onChange([...next]);
  };
  const remove = (ids: string[]) => {
    const drop = new Set(ids);
    onChange(selectedResourceIds.filter((id) => !drop.has(id)));
  };

  if (loading === 'loading') return <Loader />;
  if (loading === 'error')
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text="Could not load chapter resources."
      />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(20), md: pxTovW(30) },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: pxToRem(16), md: pxTovW(20) },
        }}
      >
        <Typography variant="h3">Chapter Resources</Typography>
        {groups.length === 0 ? (
          <Typography variant="bodyText">No chapter-level resources</Typography>
        ) : (
          groups.map((group) => (
            <Box key={group.title}>
              <Typography
                variant="h4"
                sx={{ mb: { xs: pxToRem(8), md: pxTovW(10) } }}
              >
                {group.title}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: pxToRem(10), md: pxTovW(16) },
                }}
              >
                {group.resources.map((resource) => {
                  const checked = selected.has(resource.resourceId);
                  return (
                    <SelectableResourceCard
                      key={resource.resourceId}
                      resource={resource}
                      checked={checked}
                      disabled={!checked && limitReached}
                      locked={locked.resourceIds.has(resource.resourceId)}
                      onToggle={() =>
                        checked
                          ? remove([resource.resourceId])
                          : add([resource.resourceId])
                      }
                      onPreview={() => onPreview(resource.resourceId)}
                    />
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: pxToRem(10), md: pxTovW(12) },
        }}
      >
        <Typography variant="h3">Topics</Typography>
        {topics.length === 0 ? (
          <Typography variant="bodyText">No topics</Typography>
        ) : (
          topics.map((topic) => (
            <TopicSection
              key={topic.topicId}
              subjectId={subjectId}
              chapterId={chapterId}
              topic={topic}
              selected={selected}
              limitReached={limitReached}
              onAdd={add}
              onRemove={remove}
              onPreview={onPreview}
            />
          ))
        )}
      </Box>
    </Box>
  );
}
